import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Info, Maximize2, X } from 'lucide-react';
import axios from 'axios';
import { ENV } from '../../utils/env.js';
import BlastRadius from '../../pages/repositories/BlastRadius/BlastRadius';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../ui/breadcrumb';
import { useS3Store } from '../../pages/integrations/S3Page/S3Page.jsx';

const GenericBlastRadiusPage = ({ type = 'repo' }) => {
  // Get params based on type
  const params = useParams();
  const { repoId, insightId, bucketId, findingId } = params;
  
  // Always call the hook, but only use it when type is 's3'
  const s3Store = useS3Store();
  const buckets = type === 's3' ? s3Store.buckets : null;
  
  // Determine which IDs to use based on type
  const parentId = type === 's3' ? bucketId : repoId;
  const itemId = type === 's3' ? findingId : insightId;
  
  const [showInfo, setShowInfo] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [parentName, setParentName] = useState('');
  const [isLoadingParentName, setIsLoadingParentName] = useState(true);

  useEffect(() => {
    const fetchParentName = async () => {
      try {
        if (type === 's3') {
          // Try to get from the store first
          const bucketData = buckets?.find((b) => (b.Id || b.id || b.name) === parentId);
          
          if (bucketData?.name) {
            setParentName(bucketData.name);
            setIsLoadingParentName(false);
            return;
          }

          // Use bucketId as name directly (no API call needed)
          setParentName(parentId || 'bucket');
        } else {
          // Use repoId as name directly (no API call needed)
          setParentName(parentId || 'repository');
        }
      } catch (error) {
        console.error(`Error fetching ${type === 's3' ? 'bucket' : 'repository'} name:`, error);
        setParentName(parentId);
      } finally {
        setIsLoadingParentName(false);
      }
    };

    if (parentId) {
      fetchParentName();
    }
  }, [parentId, type, buckets]);

  // Determine breadcrumb paths based on type
  const breadcrumbConfig = type === 's3' 
    ? {
        secondLevel: { path: '/identity-management', label: 'Identity Management' },
        thirdLevel: { path: '/integration/s3', label: 'S3' },
        parentPath: `/integration/s3/${parentId}`,
      }
    : {
        secondLevel: { path: '/integration/github', label: 'GitHub' },
        thirdLevel: null,
        parentPath: `/integration/github/${parentId}`,
      };

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden p-3 sm:p-4 md:p-6  gap-4">
      <div className="flex-shrink-0">
        <div className="mb-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={breadcrumbConfig.secondLevel.path}>
                    {breadcrumbConfig.secondLevel.label}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbConfig.thirdLevel && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={breadcrumbConfig.thirdLevel.path}>
                        {breadcrumbConfig.thirdLevel.label}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={breadcrumbConfig.parentPath}>
                    {isLoadingParentName ? (
                      <span className="animate-pulse bg-gray-200 h-4 w-24 rounded inline-block"></span>
                    ) : (
                      parentName || parentId
                    )}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {isLoadingParentName ? (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <span className="animate-pulse bg-gray-200 h-4 w-20 rounded inline-block"></span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              ) : (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Blast Radius</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="flex items-center gap-3">
          <Link 
            to={breadcrumbConfig.parentPath} 
            className="text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-black text-2xl md:text-3xl font-bold">Blast Radius</h1>
        </div>
      </div>
      
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
            <div />
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                  title={`Explore the contextual blast radius. Click the canvas to collapse details.`}
                  aria-label="Blast Radius info"
                  onMouseEnter={() => setShowInfo(true)}
                  onMouseLeave={() => setShowInfo(false)}
                  onClick={() => setShowInfo((v) => !v)}
                >
                  <Info className="w-4 h-4" />
                </button>
                {showInfo && (
                  <div className="absolute right-0 mt-2 w-80 max-w-[80vw] bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-700 z-10">
                    Explore the contextual blast radius for this {type === 's3' ? 'S3' : ''} finding. Use the +/− badges on nodes to expand/collapse.
                    Click the canvas to collapse the bottom details panels.
                  </div>
                )}
              </div>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-colors"
                title="Fullscreen"
                aria-label="Toggle fullscreen"
                onClick={() => setIsFullscreen(true)}
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 w-full overflow-hidden">
            <BlastRadius 
              insightId={type === 'repo' ? itemId : undefined}
              findingId={type === 's3' ? itemId : undefined}
              type={type}
            />
          </div>
        </div>
      </div>
      
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          {/* Fullscreen Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Blast Radius</h2>
              {parentName && (
                <span className="text-sm text-gray-500">• {parentName}</span>
              )}
            </div>
            <button
              type="button"
              className="p-2 rounded hover:bg-gray-100 text-gray-600 transition-colors"
              title="Exit fullscreen"
              aria-label="Exit fullscreen"
              onClick={() => setIsFullscreen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Fullscreen Content */}
          <div className="flex-1 min-h-0 overflow-hidden bg-gray-50">
            <div className="h-full w-full">
              <BlastRadius 
                insightId={type === 'repo' ? itemId : undefined}
                findingId={type === 's3' ? itemId : undefined}
                type={type}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericBlastRadiusPage;
